import { useCallback, useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import { toast, askPrompt } from "../../../lib/ui";

// Structural copy of ChatLayout's folder shape (ground rule 5: no back-imports).
type ChannelFolder = { id: string; name: string; position?: number };

type UseChannelFoldersOptions = {
  currentVoidId: string;
  token: string;
};

/**
 * Channel-folder state and lifecycle (step 5.1a — the first, self-contained
 * slice of the channels cluster): `channelFolders` plus the load effect that
 * refreshes it when the space changes / after a fresh login, and the two
 * actions that own folder-state mutations (`handleFolderReorder` drag-reorder,
 * `createFolderInline`).
 *
 * `setChannelFolders` is returned because `switchVoid` (navigation, staying in
 * the composition layer per 4.3) also loads folders for the space it opens.
 *
 * Out of scope (later 5.1 slices, channel-side — they read `channels`/write
 * `chanOrder`/emit `channel:list`, never `channelFolders`):
 * `handleChannelReorder`, `moveChannelToFolder`, `quickAddChannelToFolder`.
 * Consumers (`sidebarChannels` memo, ChannelSidebar)
 * stay in ChatLayout and read this hook's returned `channelFolders`.
 */
export function useChannelFolders({ currentVoidId, token }: UseChannelFoldersOptions) {
  const [channelFolders, setChannelFolders] = useState<ChannelFolder[]>([]);

  // Keep folders in sync when space changes or after a fresh login
  useEffect(() => {
    const tok = getAuthToken() || '';
    if (!tok || !currentVoidId || currentVoidId.startsWith('dm_')) { setChannelFolders([]); return; }
    (async () => {
      try {
        const res = await api.getAuth(`/spaces/${encodeURIComponent(currentVoidId)}/folders`, tok);
        setChannelFolders(res.folders || []);
      } catch { setChannelFolders([]); }
    })();
  }, [currentVoidId, token]);

  const handleFolderReorder = useCallback(async (folderId: string, beforeFolderId: string | null) => {
    if (!currentVoidId) return;
    const ordered = [...channelFolders].sort((a,b)=> (Number(b.position||0) - Number(a.position||0)));
    const filtered = ordered.filter(f => f.id !== folderId);
    const idx = beforeFolderId ? filtered.findIndex(f => f.id === beforeFolderId) : -1;
    const moving = channelFolders.find(f => f.id === folderId);
    if (!moving) return;
    const nextOrder = [...filtered];
    if (idx >= 0) nextOrder.splice(idx, 0, moving); else nextOrder.push(moving);
    const withPositions = nextOrder.map((f, i) => ({ ...f, position: (nextOrder.length - i) * 10 }));
    setChannelFolders(withPositions);
    try {
      const tok = getAuthToken() || '';
      for (let i = 0; i < withPositions.length; i++) {
        const pos = withPositions[i].position ?? (withPositions.length - i) * 10;
        await api.patchAuth(
          `/spaces/${encodeURIComponent(currentVoidId)}/folders/${encodeURIComponent(withPositions[i].id)}`,
          { position: pos },
          tok
        );
      }
      const res = await api.getAuth(`/spaces/${encodeURIComponent(currentVoidId)}/folders`, tok);
      setChannelFolders(res.folders || []);
    } catch (e:any) {
      // Revert optimistic update on failure
      const res = await api.getAuth(`/spaces/${encodeURIComponent(currentVoidId)}/folders`, getAuthToken() || '');
      setChannelFolders(res.folders || []);
      toast(e?.message || 'Failed to reorder folders', 'error');
    }
  }, [currentVoidId, channelFolders]);

  async function createFolderInline() {
    if (!currentVoidId) return;
    const name = (await askPrompt({ title: 'New folder', label: 'Folder name' }))?.trim();
    if (!name) return;
    try {
      const tok = getAuthToken() || '';
      await api.postAuth(`/spaces/${encodeURIComponent(currentVoidId)}/folders`, { name, position: (channelFolders.length + 1) * 10 }, tok);
      const res = await api.getAuth(`/spaces/${encodeURIComponent(currentVoidId)}/folders`, tok);
      setChannelFolders(res.folders || []);
    } catch (e:any) {
      toast(e?.message || 'Failed to create folder', 'error');
    }
  }

  return {
    channelFolders,
    setChannelFolders,
    handleFolderReorder,
    createFolderInline,
  };
}
